import { ImportData } from 'features/importerv2/importer-types'

export type WizardStep = 'upload' | 'map' | 'validate' | 'review'

/**
 * How rows whose email already exists in the org's CRM are handled at import:
 * - 'keep'   (default) — link + skip the existing contact; no field changes.
 * - 'update'           — overwrite the existing contact with provided values
 *                        (blank cells never overwrite existing data).
 */
export type ExistingContactMode = 'keep' | 'update'

export interface WizardState {
  step: WizardStep
  file: File | null
  importData: ImportData | null
  importNumber: number | null
  /**
   * Signature of the column mapping that produced the current prepared
   * preview. Used by MapColumnsStep to decide whether Next can reuse the
   * existing preview (unchanged mapping) or must re-prepare it in place
   * (changed mapping), instead of always creating a new one.
   */
  preparedMappingSig: string | null
  excludedRowIds: Set<string>
  existingContactMode: ExistingContactMode
  /** Campaign this import is locked to (campaign entry point), else null. */
  campaignId: number | null
  /** CSV header -> new manual campaign-custom-column name, from the Map step. */
  customColumns: { header: string; name: string }[]
  result: { created: number; excluded: number; failed: number } | null
}

export type WizardAction =
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'SET_FILE'; file: File }
  | { type: 'SET_IMPORT_DATA'; data: ImportData }
  | { type: 'SET_IMPORT_NUMBER'; importNumber: number }
  | { type: 'SET_PREPARED_MAPPING_SIG'; sig: string }
  | { type: 'TOGGLE_ROW_EXCLUDED'; rowId: string }
  | { type: 'SET_EXCLUDED_ROWS'; rowIds: Set<string> }
  | { type: 'SET_EXISTING_CONTACT_MODE'; mode: ExistingContactMode }
  | {
      type: 'SET_CUSTOM_COLUMNS'
      customColumns: { header: string; name: string }[]
    }
  | { type: 'SET_RESULT'; result: WizardState['result'] }
  | { type: 'RESET' }

export const initialWizardState: WizardState = {
  step: 'upload',
  file: null,
  importData: null,
  importNumber: null,
  preparedMappingSig: null,
  excludedRowIds: new Set(),
  existingContactMode: 'keep',
  campaignId: null,
  customColumns: [],
  result: null,
}

export function wizardReducer(
  state: WizardState,
  action: WizardAction
): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step }
    case 'SET_FILE':
      return { ...state, file: action.file }
    case 'SET_IMPORT_DATA':
      return { ...state, importData: action.data }
    case 'SET_IMPORT_NUMBER':
      return { ...state, importNumber: action.importNumber }
    case 'SET_PREPARED_MAPPING_SIG':
      return { ...state, preparedMappingSig: action.sig }
    case 'TOGGLE_ROW_EXCLUDED': {
      const next = new Set(state.excludedRowIds)
      if (next.has(action.rowId)) {
        next.delete(action.rowId)
      } else {
        next.add(action.rowId)
      }
      return { ...state, excludedRowIds: next }
    }
    case 'SET_EXCLUDED_ROWS':
      return { ...state, excludedRowIds: new Set(action.rowIds) }
    case 'SET_EXISTING_CONTACT_MODE':
      return { ...state, existingContactMode: action.mode }
    case 'SET_CUSTOM_COLUMNS':
      return { ...state, customColumns: action.customColumns }
    case 'SET_RESULT':
      return { ...state, result: action.result }
    case 'RESET':
      // Preserve the campaign lock: a "start over" on a locked
      // /contacts/import?campaign=<id> flow must not silently drop the lock
      // (which would re-expose the destination picker while the URL still
      // scopes the import to that campaign).
      return { ...initialWizardState, campaignId: state.campaignId }
    default:
      return state
  }
}
